import { v4 as uuid } from 'uuid'
import { createApp, computed } from 'vue'
import type { SearchResultItem } from '@onxmaps/search-web'
import searchResultMarker from '@/components/search/search-result-marker.vue'
import { getGlyphIconMap } from '@/services/search-service'
import { GLYPH_ICON_TYPES } from '@/lib/glyph-markers-constants'

const glyphMap = computed(() => getGlyphIconMap())

interface MarkerFeature {
  id: string
  searchId: string
  location: [number, number]
  element: HTMLElement
}

/**
 * Create array of map feature elements displaying glyph characters
 * @param results - Array of search results
 * @param markerStyle - Style of the marker
 * @param isSelected - Whether the marker is selected
 * @returns Array of feature objects to add to map markers
 */
export const generateGlyphMarkers = (
  results: SearchResultItem[],
  markerStyle: string,
  isSelected = false
): MarkerFeature[] => {
  return results.map((result) => {
    // Create marker root element
    const el = document.createElement('div')
    el.className = 'search-result-marker'

    // Mount Vue component in that marker element
    const markerComponent = createApp(searchResultMarker, {
      resultId: result.item.id,
      resultType: result.item.onxType,
      markerStyle,
      isSelected,
    })
    markerComponent.mount(el)

    return {
      id: uuid(),
      searchId: result.item.id,
      location: [
        result.item.point?.coordinates[0] ?? 0,
        result.item.point?.coordinates[1] ?? 0,
      ],
      element: el,
    }
  })
}

/**
 * *(AI-generated)*
 * Returns the default information icon glyph.
 */
export const getDefaultIcon = (): string | undefined => {
  return (
    glyphMap.value?.['INFORMATION'] as Record<string, string> | undefined
  )?.[GLYPH_ICON_TYPES.ICON]
}

/**
 * *(AI-generated)*
 * Returns the glyph value for a given icon name and type.
 */
export const getGlyph = (icon: string, type: string): string | undefined => {
  return (glyphMap.value?.[icon] as Record<string, string> | undefined)?.[type]
}

/**
 * Get the blank icon by a specific icon type
 * @param type - type of icon to display, defined from GLYPH_ICON_TYPES
 * @returns blank icon glyph value
 */
export const getBlankIcon = (type: string): string | undefined => {
  return (glyphMap.value?.['BLANK'] as Record<string, string> | undefined)?.[
    type
  ]
}

/**
 * Get the corresponding glyph icon for a search result type
 * @param type - search result type
 * @returns glyph icon
 */
export const getGlyphIconFromSearchResultType = (
  type?: string
): string | undefined => {
  if (!type) {
    return getDefaultIcon()
  }

  const uppercaseIcon = type.toUpperCase()
  const iconData = glyphMap.value[uppercaseIcon]
  if (iconData && typeof iconData === 'object') {
    return (iconData as Record<string, string>)[GLYPH_ICON_TYPES.ICON]
  }

  return getDefaultIcon()
}

/**
 * *(AI-generated)*
 * Get a glyph rune character for a given search result type
 * @param type - search result type
 * @returns glyph rune character
 */
export const getGlyphRuneFromSearchResultType = (type?: string): string => {
  const htmlEntity = getGlyphIconFromSearchResultType(type)

  const hex = htmlEntity?.slice(3, -1)
  const glyphCharacter = String.fromCharCode(parseInt(hex ?? '', 16))

  return glyphCharacter
}
